import type { NavigateFunction } from "react-router";
import {
  buildPropertyLookupAddressIdentity,
  parseAddressNavigationLocation,
  type AddressNavigationLocation,
  type StructuredAddressIdentity,
} from "../utils/addressNavigation";
import { sanitizeLookupTraceId } from "./lookupTelemetry";
import { isCountyGuideCheckContext, type CountyGuideCheckContext } from "./countyGuideCheck";
import {
  BERGEN_ORIGINAL_PROPERTY_SPEND_POLICY,
  PROVIDER_SPEND_POLICY_SESSION_KEY,
  browserProviderSpendPolicyStatus,
  type ProviderSpendPolicy,
} from "../../supabase/functions/_shared/provider-spend-policy";

const STORAGE_PREFIX = "fairpath_property_lookup_v1:";
const KEY_FIELD = "propertyLookupKey";
const LOOKUP_KEY = /^[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/i;
const PRIVATE_PARAMS = [
  "address", "street", "unit", "city", "county", "state", "zip", "country",
  "lat", "lng", "address_provider", "place_id", "lookup_trace_id", "lookup_source",
  "provider_spend_policy",
] as const;
const ATTRIBUTION_PARAMS = [
  "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "oppref",
] as const;

/** Carry only public campaign identifiers across an internal route transition. */
export function preserveCurrentAttribution(href: string): string {
  if (typeof window === "undefined" || !href.startsWith("/")) return href;
  const destination = new URL(href, window.location.origin);
  const currentParams = new URLSearchParams(window.location.search);
  for (const key of ATTRIBUTION_PARAMS) {
    const value = currentParams.get(key);
    if (value) destination.searchParams.set(key, value);
  }
  return `${destination.pathname}${destination.search}${destination.hash}`;
}

export interface PropertyCheckContext {
  addressIdentity: StructuredAddressIdentity;
  lookupTraceId: string | null;
  lookupSource: string | null;
  providerSpendPolicy?: ProviderSpendPolicy;
  countyGuide?: CountyGuideCheckContext;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function isContext(value: unknown): value is PropertyCheckContext {
  if (!isRecord(value) || !isRecord(value.addressIdentity)) return false;
  const identity = value.addressIdentity;
  return typeof identity.formattedAddress === "string" &&
    Boolean(identity.formattedAddress.trim()) &&
    ["street", "unit", "city", "county", "state", "zip", "country", "providerPlaceId"]
      .every((key) => identity[key] === null || typeof identity[key] === "string") &&
    ["latitude", "longitude"].every((key) => identity[key] === null ||
      (typeof identity[key] === "number" && Number.isFinite(identity[key]))) &&
    (identity.provider === null || identity.provider === "google_places" || identity.provider === "google_geocoder") &&
    (value.lookupTraceId === null || typeof value.lookupTraceId === "string") &&
    (value.lookupSource === null || typeof value.lookupSource === "string") &&
    (value.countyGuide === undefined || isCountyGuideCheckContext(value.countyGuide)) &&
    (value.providerSpendPolicy === undefined ||
      value.providerSpendPolicy === BERGEN_ORIGINAL_PROPERTY_SPEND_POLICY);
}

export function getPropertyLookupKey(state: unknown): string | null {
  if (!isRecord(state)) return null;
  const key = state[KEY_FIELD];
  return typeof key === "string" && LOOKUP_KEY.test(key) ? key : null;
}

/** An explicit missing key distinguishes an expired check from the returning-user hub. */
export function hasPropertyLookupState(state: unknown): boolean {
  return isRecord(state) && Object.prototype.hasOwnProperty.call(state, KEY_FIELD);
}

export function readPropertyCheckContext(state: unknown): PropertyCheckContext | null {
  const key = getPropertyLookupKey(state);
  if (!key) return null;
  try {
    const stored: unknown = JSON.parse(window.sessionStorage.getItem(`${STORAGE_PREFIX}${key}`) ?? "null");
    if (!isRecord(stored) || stored.version !== 1 || !isContext(stored.context)) return null;
    return stored.context;
  } catch {
    return null;
  }
}

/** No latest-search fallback: failure produces an address-entry navigation. */
export function createPropertyCheckState(
  address: string,
  location?: AddressNavigationLocation | null,
  metadata: {
    lookupTraceId?: string | null;
    lookupSource?: string | null;
    providerSpendPolicy?: ProviderSpendPolicy;
    providerSpendPolicyInvalid?: boolean;
    countyGuide?: CountyGuideCheckContext;
  } = {},
): {
  propertyLookupKey: string | null;
  providerSpendPolicy?: ProviderSpendPolicy;
  providerSpendPolicyInvalid?: true;
} {
  const currentPolicyStatus = browserProviderSpendPolicyStatus();
  const preserveInvalid = metadata.providerSpendPolicyInvalid === true ||
    currentPolicyStatus === "invalid";
  const effectiveProviderSpendPolicy = !preserveInvalid
    ? metadata.providerSpendPolicy ?? (currentPolicyStatus === "active"
      ? BERGEN_ORIGINAL_PROPERTY_SPEND_POLICY
      : undefined)
    : undefined;
  const statePolicy = preserveInvalid
    ? { providerSpendPolicyInvalid: true as const }
    : effectiveProviderSpendPolicy
      ? { providerSpendPolicy: effectiveProviderSpendPolicy }
      : {};
  const context: PropertyCheckContext = {
    addressIdentity: buildPropertyLookupAddressIdentity(address, location),
    lookupTraceId: sanitizeLookupTraceId(metadata.lookupTraceId),
    lookupSource: metadata.lookupSource ?? null,
    ...(metadata.countyGuide ? { countyGuide: metadata.countyGuide } : {}),
    ...(effectiveProviderSpendPolicy
      ? { providerSpendPolicy: effectiveProviderSpendPolicy }
      : {}),
  };
  try {
    if (!isContext(context)) return { propertyLookupKey: null, ...statePolicy };
    const key = crypto.randomUUID();
    const serialized = JSON.stringify({ version: 1, context });
    window.sessionStorage.setItem(`${STORAGE_PREFIX}${key}`, serialized);
    if (effectiveProviderSpendPolicy) {
      window.sessionStorage.setItem(
        PROVIDER_SPEND_POLICY_SESSION_KEY,
        effectiveProviderSpendPolicy,
      );
    } else if (preserveInvalid) {
      window.sessionStorage.setItem(PROVIDER_SPEND_POLICY_SESSION_KEY, "invalid");
    } else {
      window.sessionStorage.removeItem(PROVIDER_SPEND_POLICY_SESSION_KEY);
    }
    // Some restricted browsers silently discard writes instead of throwing.
    if (window.sessionStorage.getItem(`${STORAGE_PREFIX}${key}`) !== serialized) {
      return { propertyLookupKey: null, ...statePolicy };
    }
    return { propertyLookupKey: key, ...statePolicy };
  } catch {
    return { propertyLookupKey: null, ...statePolicy };
  }
}

export function navigateToPropertyCheck(
  navigate: NavigateFunction,
  address: string,
  location?: AddressNavigationLocation | null,
  options: {
    lookupTraceId?: string | null;
    lookupSource?: string | null;
    providerSpendPolicy?: ProviderSpendPolicy;
    replace?: boolean;
    countyGuide?: CountyGuideCheckContext;
    path?: "/check" | "/dashboard";
  } = {},
): void {
  const state = createPropertyCheckState(address, location, options);
  void navigate(preserveCurrentAttribution(options.path ?? "/check"), {
    state,
    replace: options.replace,
  });
}

export function hasLegacyPropertyCheckContext(pathname: string, search: string): boolean {
  if (pathname !== "/check" && pathname !== "/dashboard") return false;
  const params = new URLSearchParams(search);
  return PRIVATE_PARAMS.some((key) => params.has(key));
}

/** Exact routes only: QR codes, saved properties, and wizard resume keep their own contracts. */
export function migrateLegacyPropertyCheck(
  pathname: string,
  search: string,
  state: unknown,
): { search: string; state: Record<string, unknown> } | null {
  if (!hasLegacyPropertyCheckContext(pathname, search)) return null;
  const params = new URLSearchParams(search);
  const identity = parseAddressNavigationLocation(params);
  const policyParamPresent = params.has("provider_spend_policy");
  const policyParam = params.get("provider_spend_policy");
  const lookupState = createPropertyCheckState(identity.formattedAddress, identity, {
    lookupTraceId: params.get("lookup_trace_id"),
    lookupSource: params.get("lookup_source"),
    ...(policyParam === BERGEN_ORIGINAL_PROPERTY_SPEND_POLICY
      ? { providerSpendPolicy: BERGEN_ORIGINAL_PROPERTY_SPEND_POLICY }
      : policyParamPresent ? { providerSpendPolicyInvalid: true } : {}),
  });
  for (const key of PRIVATE_PARAMS) params.delete(key);
  return {
    search: params.size ? `?${params.toString()}` : "",
    state: { ...(isRecord(state) ? state : {}), ...lookupState },
  };
}

/** Must run before the router snapshots history and before monitoring modules evaluate. */
export function capturePropertyCheckFromCurrentUrl(): void {
  if (typeof window === "undefined") return;
  const { pathname, search, hash } = window.location;
  const historyState: unknown = window.history.state;
  const previous = isRecord(historyState) ? historyState : {};
  const migrated = migrateLegacyPropertyCheck(pathname, search, previous.usr);
  if (!migrated) return;
  window.history.replaceState(
    { ...previous, usr: migrated.state },
    "",
    `${pathname}${migrated.search}${hash}`,
  );
}
